import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, ChevronLeft, ChevronRight } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCallsList } from "@/hooks/useCallsList";
import { useAppUsers } from "@/hooks/useAppUsers";
import { useCurrentAppUser } from "@/hooks/useCurrentAppUser";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { safeFormat } from '@/lib/safeDate';
import { ptBR } from "date-fns/locale";

const formatDuration = (seconds: number | null) => {
  if (!seconds || seconds <= 0) return "-";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const getStatusBadge = (status: string, displayStatus?: string) => {
  const s = displayStatus || status;
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    completed: { label: "Completada", variant: "default" },
    "in-progress": { label: "Em andamento", variant: "secondary" },
    "no-answer": { label: "Sem resposta", variant: "destructive" },
    busy: { label: "Ocupado", variant: "destructive" },
    failed: { label: "Falha", variant: "destructive" },
    canceled: { label: "Cancelada", variant: "outline" },
    ringing: { label: "Tocando", variant: "secondary" },
    queued: { label: "Na fila", variant: "outline" },
  };
  const info = map[s] || { label: s, variant: "outline" as const };
  return <Badge variant={info.variant}>{info.label}</Badge>;
};

const getDirectionInfo = (direction: string) => {
  if (direction?.startsWith("outbound")) {
    return { label: "Saída", icon: <PhoneOutgoing className="h-3.5 w-3.5 text-primary" />, color: "text-primary" };
  }
  return { label: "Entrada", icon: <PhoneIncoming className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />, color: "text-green-600 dark:text-green-400" };
};

export default function Calls() {
  const [dateRange, setDateRange] = useState("week");
  const [selectedUser, setSelectedUser] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data: currentAppUser } = useCurrentAppUser();
  const currentUserId = currentAppUser?.id || null;
  const { isAdmin } = useUserPermissions();
  const { users: appUsers } = useAppUsers();

  // Date range
  const dateFilters = useMemo(() => {
    const now = new Date();
    switch (dateRange) {
      case "today":
        return { startDate: startOfDay(now), endDate: endOfDay(now) };
      case "week":
        return { startDate: startOfWeek(now, { locale: ptBR }), endDate: endOfWeek(now, { locale: ptBR }) };
      case "month":
        return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
      default:
        return {};
    }
  }, [dateRange]);

  // Determine userIds filter
  const userIds = useMemo(() => {
    if (isAdmin) {
      if (selectedUser === "all") return undefined;
      if (selectedUser === "current") return currentUserId ? [currentUserId] : undefined;
      return [selectedUser];
    }
    return currentUserId ? [currentUserId] : undefined;
  }, [isAdmin, selectedUser, currentUserId]);

  const { calls, loading, error, totalCount, page, setPage, pageSize } = useCallsList({
    ...dateFilters,
    userIds,
    direction: directionFilter !== "all" ? (directionFilter as "inbound" | "outbound") : null,
  });

  // Client-side status filter
  const filteredCalls = useMemo(() => {
    if (statusFilter === "all") return calls;
    if (statusFilter === "missed") {
      return calls.filter(c => ["no-answer", "busy", "failed"].includes(c.displayStatus || c.status));
    }
    return calls.filter(c => (c.displayStatus || c.status) === statusFilter);
  }, [calls, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    return {
      outbound: calls.filter(c => c.direction?.startsWith("outbound") && (c.displayStatus || c.status) === "completed").length,
      inbound: calls.filter(c => c.direction === "inbound" && (c.displayStatus || c.status) === "completed").length,
      missed: calls.filter(c => ["no-answer", "busy", "failed"].includes(c.displayStatus || c.status)).length,
    };
  }, [calls]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Chamadas</h1>
        <p className="text-muted-foreground">Histórico de chamadas</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Realizadas</CardTitle>
            <PhoneOutgoing className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.outbound}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Recebidas</CardTitle>
            <PhoneIncoming className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.inbound}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Perdidas</CardTitle>
            <PhoneMissed className="w-4 h-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{stats.missed}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 flex-wrap">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Hoje</SelectItem>
                <SelectItem value="week">Esta Semana</SelectItem>
                <SelectItem value="month">Este Mês</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>

            <Select value={directionFilter} onValueChange={setDirectionFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Direções</SelectItem>
                <SelectItem value="inbound">Entrada</SelectItem>
                <SelectItem value="outbound">Saída</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                <SelectItem value="completed">Completada</SelectItem>
                <SelectItem value="missed">Perdida</SelectItem>
                <SelectItem value="in-progress">Em andamento</SelectItem>
                <SelectItem value="canceled">Cancelada</SelectItem>
              </SelectContent>
            </Select>

            {isAdmin && (
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Usuário" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Minhas Chamadas</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                  {appUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.display_name || user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Calls Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Chamadas
            <span className="text-sm font-normal text-muted-foreground">({totalCount})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">Erro ao carregar chamadas: {error}</div>
          ) : filteredCalls.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma chamada encontrada
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contato</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Direção</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Data/Hora</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCalls.map(call => {
                    const contactName = call.contact
                      ? [call.contact.first_name, call.contact.last_name].filter(Boolean).join(" ")
                      : null;
                    const phone = call.direction?.startsWith("outbound") ? call.to_e164 : call.from_e164;
                    const dir = getDirectionInfo(call.direction);

                    return (
                      <TableRow key={call.id}>
                        <TableCell className="font-medium">
                          {contactName || <span className="text-muted-foreground">Desconhecido</span>}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{phone || "-"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {dir.icon}
                            <span className={`text-sm ${dir.color}`}>{dir.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(call.status, call.displayStatus)}</TableCell>
                        <TableCell className="text-sm">{formatDuration(call.duration_seconds)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {safeFormat(new Date(call.created_at), "dd/MM/yyyy HH:mm")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">
                    Página {page} de {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Próxima
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
